<style lang="scss">
@use "assets/scss/components/header.scss";
</style>

<template>
  <header class="white-section !px-5">
    <div class="flex flex-1 justify-between">
      <div class="left">
        <img :alt="t('header.logo')" class="h-12" src="/images/logo.png">
      </div>
      <div class="right flex gap-6">
        <CommandSearch v-if="!isPrinter && $account" :nav-links="navLinks" />
        <UPopover v-if="!isPrinter && $account && $account.subscription_plan" v-model:open="PopoverOpen" class="popup">
          <div>
            <Button className="z-10 px-4 h-10" type="default" :text="t('header.plan')" icon="i-heroicons-bolt" />
          </div>

          <!-- The plan and its state, the money, the invoices, and a discreet way out (components/billing/PlanMenu.vue). -->
          <template #content>
            <PlanMenu @navigate="PopoverOpen = false" @cancel="PopoverOpen = false" />
          </template>
        </UPopover>
        <div v-if="!isPrinter && $account" class="dropdown">
          <UDropdownMenu :items="items" :content="{ side: 'bottom-start' }" color="neutral" variant="outline">
            <div class="flex gap-4 items-center cursor-pointer">
              <VsAvatar badge badgeColor="success"
                        class="cursor-pointer !rounded-full" size="45">
                <NuxtImg :src="gravatarUrl"></NuxtImg>
              </VsAvatar>
              <div class="text-base/6">
                <h6 class="text-h6 d-flex align-center text-black font-weight-semibold">{{ $user.firstname }}
                  {{ truncate($user.lastname, 1, ".") }}</h6>
                <span class="text-subtitle-2 font-weight-medium text-grey-100 max-w-12 text-ellipsis">{{
                    $account?.displayname || t("header.noCompany")
                  }}</span>
              </div>
            </div>
          </UDropdownMenu>
        </div>
      </div>
    </div>
    <HelpDialog />
    <CancelPlanDialog />
  </header>
</template>

<script setup>
import { textToSha256, truncate } from "../utils/global.mjs";
import { fetch } from "~/utils/helper";
import { VsAvatar } from "vuesax-ts";
import { feedbackIntegration } from "@sentry/vue";
import HelpDialog from "~/components/onboarding/HelpDialog.vue";
import PlanMenu from "~/components/billing/PlanMenu.vue";
import CancelPlanDialog from "~/components/billing/CancelPlanDialog.vue";

const PopoverOpen = ref(false);

const { t } = useI18n();
const { $account, $user } = useNuxtApp();
const { ask } = useHelpDesk();
const { languages, locale, setLanguage } = useLanguage();
const route = useRoute();

const gravatarUrl = ref("https://gravatar.com/avatar/000000000000000000000000000000000000000000000000000000?d=mp");


watch(() => PopoverOpen.value, (value) => {
  if (value) {
    const auth = useAuth();
    auth.refreshAuthStatus();
  }
});

const reportBug = async () => {
  const feedback = feedbackIntegration({
    autoInject: false,
    useSentryUser: {
      email: $user.email,
      username: $user.firstname + " " + $user.lastname
    },
    showName: false,
    showEmail: false
  });
  const form = await feedback.createForm();
  form.appendToDom();
  form.open();
};

// The account menu, in the person's language: rebuilt when it changes. The switch between companies shows once the
// person has more than one; the administrators' way to the management, or back, comes first.
const userAccounts = ref([]);
const items = computed(() => {
  const hasSubscription = Boolean($account?.subscription_plan);
  const groups = [];
  if ($user?.isAdmin) {
    groups.push([route.path.startsWith("/management")
      ? { label: t("header.menu.userInterface"), icon: "i-heroicons-arrow-left-on-rectangle", onSelect: () => { window.location.href = "/"; } }
      : { label: t("header.menu.management"), icon: "i-heroicons-arrow-left-on-rectangle", onSelect: () => { window.location.href = "/management"; } }]);
  }
  if (userAccounts.value.length > 1) {
    groups.push([{ label: t("header.menu.switchAccount"), icon: "i-heroicons-arrows-right-left-20-solid", onSelect: () => switchAccount() }]);
  }
  groups.push(
    [{ label: t("header.menu.account"), icon: "i-heroicons-cog-8-tooth", onSelect: () => navigateTo("/settings/user") }],
    [
      { label: t("header.menu.company"), icon: "i-heroicons-building-office-2-20-solid", onSelect: () => navigateTo("/settings/account") },
      { label: t("header.menu.team"), icon: "i-heroicons-users-20-solid", onSelect: () => navigateTo("/team") },
      // Unconditional entry: it was the only path to /settings/billing, and restricting it to subscribers made invoices
      // unreachable for a cancelled account, even though the route and the page serve them just fine.
      ...($account ? [{
        label: hasSubscription ? t("header.menu.myPlan") : t("header.menu.invoices"),
        icon: "i-heroicons-credit-card-20-solid",
        onSelect: () => navigateTo(hasSubscription ? "/settings/billing" : "/settings/billing#invoices_history")
      }] : [])
    ],
    [{ label: t("header.menu.developers"), icon: "i-heroicons-wrench-20-solid", onSelect: () => navigateTo("/developers") }],
    [
      // A person, not a ticket: the question reaches the team (composables/useHelpDesk.js), answered by e-mail or phone.
      { label: t("header.menu.help"), icon: "i-heroicons-chat-bubble-left-right", onSelect: () => ask() },
      { label: t("header.menu.bug"), icon: "i-heroicons-information-circle", onSelect: reportBug }
    ],
    // The dashboard's language, each one named in its own words (utils/locale.js), the current one checked.
    [{
      label: `${ t("header.menu.language") } · ${ languages.find((language) => language.code === locale.value)?.name ?? "" }`,
      icon: "i-heroicons-language",
      children: languages.map((language) => ({
        label: language.name,
        icon: language.code === locale.value ? "i-heroicons-check" : undefined,
        onSelect: () => setLanguage(language.code)
      }))
    }],
    [{ label: t("header.menu.logout"), icon: "i-heroicons-arrow-left-on-rectangle", onSelect: () => logout() }]
  );
  return groups;
});
let $swal = null;

onBeforeMount(async () => {
  if ($user?.email) {
    gravatarUrl.value = `https://www.gravatar.com/avatar/${ await textToSha256($user?.email) }?d=initials&initials=${ $user.firstname[0] }${ $user.lastname[0] }`;
  } else if ($user?.firstname && $user?.lastname) {
    gravatarUrl.value = `https://www.gravatar.com/avatar/000000000000000000000000000000000000000000000000000000?d=initials&initials=${ $user.firstname[0] }${ $user.lastname[0] }`;
  }
  try {
    userAccounts.value = (await fetchAccounts()) ?? [];
  } catch { /* the switch between companies simply stays hidden */ }
});

const switchAccount = async () => {
  if (!$swal) $swal = useNuxtApp().$swal;
  $swal.fire({
    title: t("header.switch.loading"),
    text: t("header.switch.loadingText"),
    showCancelButton: false,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    async didOpen(_) {
      const accounts = await fetchAccounts();
      // transform accounts to { id: displayname }
      $swal.fire({
        title: t("header.switch.title"),
        text: t("header.switch.text"),
        input: {
          label: t("header.switch.label"),
          type: "select",
          options: accounts.map(account => {
            return {
              value: account.id,
              label: account.displayname
            };
          }),
          value: $account.id,
          validator: (value) => {
            if (!value) {
              return t("header.switch.required");
            }
          }
        },
        showCancelButton: true,
        showConfirmButton: true,
        confirmButtonText: t("header.switch.confirm"),
        cancelButtonText: t("header.switch.cancel"),
        showLoaderOnConfirm: true,
        async preConfirm(value) {
          const data = await fetch(`/accounts/@me`, {
            method: "POST",
            body: {
              accountId: value
            }
          });
          if (data.success) {
            window.location.reload();
          } else {
            $swal.fire({
              title: t("header.switch.error"),
              text: data.message,
              icon: "error"
            });
          }
        }
      });
    }
  });
};

const fetchAccounts = async () => {
  const data = await fetch("/accounts");
  return data.accounts;
};

</script>

<script>
export default {
  name: "Header",
  props: {
    isPrinter: {
      type: Boolean,
      default: false
    },
    // The pages offered by the search palette (Ctrl+K).
    navLinks: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      fetchingAccounts: false
    };
  }
};
</script>
